// App.js
import React, { useEffect } from "react";
import { ChakraProvider, ColorModeScript, useColorMode } from "@chakra-ui/react";
import { BrowserRouter as Router } from "react-router-dom";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { GoogleApiProvider } from "./contexts/GoogleApiContext";
import { CheckoutProvider } from "./contexts/CheckoutContext";
import AppRoutes from "./routes/AppRoutes";
import { GoogleOAuthProvider } from "@react-oauth/google";
import theme from "./theme";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import "dayjs/locale/lt"; // Import the Lithuanian locale
import "dayjs/locale/en";
import "dayjs/locale/lv";
import "dayjs/locale/pl";
import { initGA } from "./utils/ga";
import GoogleAnalyticsTracker from "./components/tracking/GoogleAnalyticsTracker";
// import HotjarInit from "./components/tracking/HotjarInit";
// import HotjarStateTracker from "./components/tracking/HotjarStateTracker";
import Clarity from "@microsoft/clarity";
import Smartlook from "smartlook-client";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY_TEST);
const ipInfoApiKey = process.env.REACT_APP_IPINFO_API_KEY;

const fetchCountryAndSetLanguage = async () => {
  try {
    const response = await fetch(
      `https://ipinfo.io/json?token=${ipInfoApiKey}`
    );
    if (!response.ok) throw new Error("Failed to fetch IP info");

    const data = await response.json();

    const countryToLanguageMap = {
      LT: "lt",
      GB: "en",
      US: "en",
    };

    // console.log("data.country", data.country);

    const detectedLanguage = countryToLanguageMap[data.country] || "en"; // Fallback to English

    // console.log("detectedLanguage", detectedLanguage);

    // Check if language is already set
    const storedLanguage = localStorage.getItem("language");
    if (!storedLanguage) {
      localStorage.setItem("language", detectedLanguage);
      return detectedLanguage;
    }

    return storedLanguage;
  } catch (error) {
    console.error("Error fetching country:", error);
    return "en"; // Fallback to English
  }
};

const ForceLightMode = () => {
  const { setColorMode } = useColorMode();

  useEffect(() => {
    setColorMode("light");
  }, [setColorMode]);

  return null;
};

const App = () => {
  const { i18n } = useTranslation();

  // INIT
  useEffect(() => {
    initGA();

    const storedLanguage = localStorage.getItem("language");

    if (storedLanguage) {
      i18n.changeLanguage(storedLanguage);
      dayjs.locale(storedLanguage);
      return; // Don't fetch again
    }

    const setLanguageFromIP = async () => {
      const language = await fetchCountryAndSetLanguage();
      i18n.changeLanguage(language);
      dayjs.locale(language);
    };

    setLanguageFromIP();
  }, []);

  // ✅ Initialize Microsoft Clarity
  useEffect(() => {
    const projectId = process.env.REACT_APP_MICROSOFT_CLARITY_PROJECT_ID;

    if (projectId) {
      Clarity.init(projectId);
    }
  }, []);

  // Add inside App component
  useEffect(() => {
    Smartlook.init(process.env.REACT_APP_SMARTLOOK_KEY, {
      region: "us", // or 'us' if applicable
      cookies: false, // optional, depending on your cookie policy
      // You can add more optional settings here as needed
    });
  }, []);

  return (
    <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
      <ChakraProvider theme={theme}>
        <ColorModeScript initialColorMode={theme.config.initialColorMode} />
        <ForceLightMode />
        <CheckoutProvider>
          <Elements stripe={stripePromise}>
            <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="lt">
              <GoogleApiProvider
                googleApiKey={process.env.REACT_APP_GOOGLE_API_KEY}
              >
                <Router>
                  <GoogleAnalyticsTracker />
                  <AppRoutes />
                </Router>
              </GoogleApiProvider>
            </LocalizationProvider>
          </Elements>
        </CheckoutProvider>
      </ChakraProvider>
    </GoogleOAuthProvider>
  );
};

export default App;
